import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingCart, Loader2, ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CART_PLANS = {
  essential: { label: "Essential", weekly: 49, setup: 49 },
  advanced: { label: "Advanced", weekly: 79, setup: 99 },
  pro: { label: "Pro", weekly: 149, setup: 199 },
  test: { label: "Test", weekly: 1, setup: 0.5 },
};

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("disjinx_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("disjinx_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((plan) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.plan === plan);
      if (existing) return prev.map((i) => (i.plan === plan ? { ...i, qty: Math.min(10, i.qty + 1) } : i));
      return [...prev, { plan, qty: 1 }];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((plan, qty) => {
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.plan !== plan) : prev.map((i) => (i.plan === plan ? { ...i, qty: Math.min(10, qty) } : i))
    );
  }, []);

  const removeItem = useCallback((plan) => setItems((prev) => prev.filter((i) => i.plan !== plan)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((a, i) => a + i.qty, 0);
  const setupTotal = items.reduce((a, i) => a + CART_PLANS[i.plan].setup * i.qty, 0);
  const weeklyTotal = items.reduce((a, i) => a + CART_PLANS[i.plan].weekly * i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, setQty, removeItem, clear, open, setOpen, count, setupTotal, weeklyTotal }}>
      {children}
    </CartContext.Provider>
  );
}

export function CartDrawer() {
  const { items, setQty, removeItem, open, setOpen, setupTotal, weeklyTotal } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkout = async () => {
    if (!user || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/payments/cart-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items, origin_url: window.location.origin }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Could not start checkout (HTTP ${res.status}) — please try again.`);
      }
      if (!res.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map((d) => d.msg).join(", ") : data.detail;
        throw new Error(detail || "Could not start checkout");
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-neutral-950/30"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col border-l border-neutral-200 bg-white"
          >
            <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-5">
              <p className="font-display text-lg font-extrabold tracking-tight">
                Your cart {items.length > 0 && <span className="text-neutral-400">({items.reduce((a, i) => a + i.qty, 0)})</span>}
              </p>
              <button data-testid="cart-close" onClick={() => setOpen(false)} aria-label="Close cart">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-lenis-prevent>
              {items.length === 0 ? (
                <div data-testid="cart-empty" className="flex h-full flex-col items-center justify-center text-center">
                  <ShoppingCart className="h-8 w-8 text-neutral-300" strokeWidth={1.5} />
                  <p className="mt-4 text-sm text-neutral-500">Your cart is empty. Add a plan to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((i) => {
                    const p = CART_PLANS[i.plan];
                    return (
                      <div key={i.plan} data-testid={`cart-item-${i.plan}`} className="border border-neutral-200 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-display text-base font-bold">{p.label} Plan</p>
                            <p className="mt-1 text-xs text-neutral-500">
                              ${p.setup.toFixed(2)} setup today · ${p.weekly.toFixed(2)}/week when live
                            </p>
                          </div>
                          <button
                            data-testid={`cart-remove-${i.plan}`}
                            onClick={() => removeItem(i.plan)}
                            aria-label={`Remove ${p.label}`}
                            className="text-neutral-400 transition-colors duration-200 hover:text-neutral-950"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                          <button
                            data-testid={`cart-qty-minus-${i.plan}`}
                            onClick={() => setQty(i.plan, i.qty - 1)}
                            className="flex h-8 w-8 items-center justify-center border border-neutral-300 transition-colors duration-200 hover:border-neutral-950"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span data-testid={`cart-qty-${i.plan}`} className="w-6 text-center text-sm font-semibold">{i.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${i.plan}`}
                            onClick={() => setQty(i.plan, i.qty + 1)}
                            className="flex h-8 w-8 items-center justify-center border border-neutral-300 transition-colors duration-200 hover:border-neutral-950"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                          <span className="ml-auto text-sm font-semibold">${(p.setup * i.qty).toFixed(2)} setup</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-neutral-200 px-6 py-6">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm font-semibold">Total due today <span className="font-normal text-neutral-500">(setup fees)</span></p>
                  <p data-testid="cart-setup-total" className="font-display text-2xl font-extrabold">${setupTotal.toFixed(2)}</p>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <p className="text-sm text-neutral-500">Expected weekly total once live</p>
                  <p data-testid="cart-weekly-total" className="text-sm font-medium text-neutral-500">${weeklyTotal.toFixed(2)}/week</p>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-neutral-400">
                  Only setup fees are charged today. Your weekly subscription begins when your automations are built and running. No hidden fees or long-term contracts.
                </p>
                {user ? (
                  <button
                    data-testid="cart-checkout-button"
                    onClick={checkout}
                    disabled={loading}
                    className="group mt-5 flex w-full items-center justify-center gap-2 bg-neutral-950 px-6 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800 disabled:opacity-50"
                  >
                    {loading ? (
                      <>Preparing checkout <Loader2 className="h-4 w-4 animate-spin" /></>
                    ) : (
                      <>Checkout securely <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></>
                    )}
                  </button>
                ) : (
                  <div data-testid="cart-signin-prompt" className="mt-5 border border-neutral-200 bg-neutral-50 p-5">
                    <div className="flex items-start gap-3">
                      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-neutral-950" />
                      <p className="text-sm leading-relaxed text-neutral-600">
                        Sign in to check out — your plans will be linked to your account so you can manage them in My Billing.
                      </p>
                    </div>
                    <Link
                      to="/signin"
                      data-testid="cart-signin-button"
                      onClick={() => setOpen(false)}
                      className="mt-4 flex w-full items-center justify-center gap-2 bg-neutral-950 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
                    >
                      Sign in with Google
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
                {error && <p data-testid="cart-error" className="mt-3 text-xs text-red-600">{error}</p>}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
